import React, { FC, useState, useEffect, useRef, ChangeEvent } from "react";
import { Stack, Box, Typography, Button } from "@mui/material";
import { TextAreaField } from "./textArea";
import { CustomFieldImageUpload } from "../../../components/fields/CustomImageUpload/CustomImageUpload";
import { FieldError } from "../../../components/fields/FieldError/FieldError";
import { InputField } from "./input";
import ErrorText from "./errorText";
import { IconCloseEye, IconEye } from "../../../utils/icons";
import { CustomTextArea } from "../../../components/fields/CustomTextArea/CustomTextArea";
import { CustomFieldLabel } from "../../../components/fields/CustomFieldLabel/CustomFieldLabel";

interface Props {
  avatar: any;
  setAvatar: (data: any) => void;
  setIntro: (data: any) => void;
  /** "Continue with Google": the email is Google's and there is no password. */
  viaGoogle?: boolean;
}
const BasicInfo: FC<Props> = ({ avatar, setAvatar, setIntro, viaGoogle }) => {
  const uploadRef = useRef<HTMLInputElement>(null);
  const [videoError, setVideoError] = useState("");
  const [fileName, setFileName] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [rows, setRows] = useState(5);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 640) { 
        setRows(11); 
        setIsMobile(true);
      } else {
        setRows(5);
      }
    };

    handleResize();
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const uploadDirectly = () => uploadRef.current?.click();
  const handleVideo = (event: ChangeEvent<HTMLInputElement>) => {
    const { files } = event.target;
    if (files && files.length > 0) {
      const target = files[0];
      if (target.size < 200000000) {
        const fReader = new FileReader();
        fReader.readAsDataURL(target);
        fReader.onloadend = async () => {
          setIntro(target);
          setVideoError("");
          setFileName(target.name);
        };
      } else setVideoError("File exceeds maximum upload size of 200MB.");
    }
  };

  const getTruncatedFileName = (fileName: string) => {
    if (isMobile && fileName.length > 40) {
      return fileName.slice(0, 40) + "...";
    }
    return fileName;
  };

  return (
    <>
      <Typography
        variant="h4"
        sx={{
          color: "var(--Base-1000, #000)",
          fontSize: "24px",
          fontStyle: "normal",
          fontWeight: 900,
          lineHeight: "132%",
        }}
      >
        Basic Info
      </Typography>
      <hr style={{ color: "var(--border)", marginTop: "16px" }} />
      <Stack
        width={1}
        direction="row"
        mb={3}
        mt={2}
        sx={{
          flexDirection: { xs: "column", sm: "row" },
        }}
      >
        <Stack width={1} spacing={2}>
          <Box className="flex w-full" sx={{ gap: 2 }}>
            <InputField
              name="firstName"
              label="First Name"
              placeholder="First Name"
            />
            <InputField
              name="lastName"
              label="Last Name"
              placeholder="Last Name"
            />
          </Box>

          <Box position="relative" className="flex w-full" sx={{ gap: 2 }}>
            <InputField name="emailAddress" placeholder="address@email.com" disabled={!!viaGoogle} />
            <Box className="w-full mt-1">
              <CustomFieldLabel>Introduction Video</CustomFieldLabel>
              <Button
                variant="contained"
                onClick={uploadDirectly}
                size="large"
                style={{ textTransform: "none", marginBottom: "4px" }}
              >
                {fileName ? `Video Details: ${getTruncatedFileName(fileName)}` : "Upload Video"}
              </Button>
              <CustomFieldLabel>
                <span className="text-text-1 text-xs">Recomended Length:</span>{" "}
                <span className="text-xs">
                  20-30 sec. 1920x1080 / 1080x1920 .mp4
                </span>
              </CustomFieldLabel>
              <ErrorText text={videoError} />
              <input
                ref={uploadRef}
                type="file"
                accept=".mp4"
                onChange={handleVideo}
                style={{ display: "none" }}
              />
            </Box>
          </Box>
          <Box className="flex w-full" sx={{ gap: 2 }}>
            <Box className="w-[49.2%]">
              <CustomTextArea
                name="description"
                label="Professional Bio"
                placeholder="Add professional bio"
                rows={rows}
                required
              />
            </Box>
            <CustomFieldImageUpload
              required
              hideInput
              name="avatar"
              label="Upload Photo"
              onChange={setAvatar}
            />
            {avatar?.error && <FieldError>{avatar.error}</FieldError>}
          </Box>

          {!viaGoogle && (
          <Box className="flex w-full" sx={{ gap: 2 }}>
            <Box position="relative" className="flex w-full" sx={{ gap: 2 }}>
              <InputField
                label="Password"
                name="password"
                type={showPassword ? "text" : "password"}
                placeholder="Password"
              />
              <Box
                position="absolute"
                // `stroke` is an SVG attribute (no var()); the token reaches
                // the icons through currentColor.
                sx={{ right: 10, top: 47, cursor: "pointer", color: "var(--text-2)" }}
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? (
                  <IconCloseEye stroke="currentColor" />
                ) : (
                  <IconEye stroke="currentColor" />
                )}
              </Box>
            </Box>

            <Box width="100%" position="relative">
              <InputField
                label="Confirm Password"
                name="passwordConfirmation"
                type={showConfirmPassword ? "text" : "password"}
                placeholder="Confirm Password"
              />
              <Box
                position="absolute"
                sx={{ right: 10, top: 47, cursor: "pointer", color: "var(--text-2)" }}
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              >
                {showConfirmPassword ? (
                  <IconCloseEye stroke="currentColor" />
                ) : (
                  <IconEye stroke="currentColor" />
                )}
              </Box>
            </Box>
          </Box>
          )}
        </Stack>
      </Stack>
    </>
  );
};

export default BasicInfo;
